/**
 * Frontend Surface Resolution — Phase SAAS-ENTRY-A1.
 *
 * The ONE place this app decides whether the current tab should render
 * the public Marketing Surface (vistoera.com / www.vistoera.com — product
 * introduction, CTA into signup/login) or the SaaS App Surface
 * (app.vistoera.com — signup/login/workspace/platform, today's existing
 * `App.tsx` routing tree, unchanged). Every other file goes through
 * `resolveFrontendSurface()` — never compare `window.location.hostname`
 * directly (enforced by
 * `backend/tests/test_frontend_navigation_contract.py`, which fails if a
 * second file starts branching on hostname on its own).
 *
 * Precedence is: an explicit `VITE_FRONTEND_SURFACE` build/dev-server env
 * var first, then hostname, then (local `vite dev` only) `'unified'`.
 *
 * A Feature Agent MAY still run the old split Feature Validation setup
 * (`npm run dev:marketing` on its own port, plain `vite`/`dev:app` on
 * another) by setting `VITE_FRONTEND_SURFACE` explicitly — see the
 * committed `.env.marketing` file for the `marketing` half. That is a
 * Feature Validation Server, not the CURRENT COMPLETE LOCAL.
 *
 * CURRENT COMPLETE LOCAL (A2 Unified Local Surface) is the *default*,
 * override-free `vite dev` session (`npm run dev`, port 5183): a single
 * origin rendering Marketing (`/`), the SaaS App (`/login`, `/signup`,
 * `/workspace/*`, ...) and Mobile Capture all via one pathname-routed
 * React Router tree (`UnifiedLocalApp` in `App.tsx`) — nobody has to
 * guess which of two ports to open. Preview/Production never set
 * `VITE_FRONTEND_SURFACE` and are never `import.meta.env.DEV`; they keep
 * today's exact hostname-based two-Pages-project split untouched.
 */

export type FrontendSurface = 'marketing' | 'app' | 'unified'

/**
 * Bare marketing-site hostnames — deliberately NOT scheme-prefixed
 * (`https://...`) and NOT one of the `api-`/`app-`/`preview-`-style
 * subdomains the Environment Portability Contract's hardcoded-host
 * scanner (`backend/tests/test_environment_portability_contract.py`)
 * flags. That scanner exists to stop a *deployment host* (which backend
 * environment to call) from leaking into business components; this set
 * is a different thing — the Surface Resolution rule itself, the one
 * sanctioned place hostnames are compared at all.
 *
 * `preview.vistoera.com` — Public Website / App Surface Contract
 * (`docs/architecture/VISTOERA_PUBLIC_SURFACE_CONTRACT.md`): Preview must
 * mirror Production's Marketing/App split so the full
 * website → login/signup → app entry path is exercisable in Preview, not
 * just Production. Its CTAs resolve (via `VITE_APP_BASE_URL`, set on the
 * Preview Cloudflare Pages project) to the separate Preview SaaS App
 * Surface host, `app-preview.vistoera.com` — never to Production's
 * `app.vistoera.com` and never to this same `preview.vistoera.com` origin.
 */
const MARKETING_HOSTNAMES = new Set(['vistoera.com', 'www.vistoera.com', 'preview.vistoera.com'])

function explicitSurfaceOverride(): FrontendSurface | null {
  const raw = (import.meta.env.VITE_FRONTEND_SURFACE as string | undefined)?.trim().toLowerCase()
  return raw === 'marketing' || raw === 'app' ? raw : null
}

/**
 * Resolves which Surface this tab should render.
 *
 * Fail-safe by design: any hostname this function doesn't specifically
 * recognize as Marketing (an unknown preview domain, a `*.pages.dev`
 * default Cloudflare URL, `127.0.0.1`, `localhost`, ...) resolves to
 * `'app'` in a real (non-`DEV`) build — today's exact existing behavior
 * — never to `'marketing'`. Getting this wrong in the Marketing
 * direction would be far worse (it would hide the real product behind a
 * brochure page); getting it wrong in the `'app'` direction is a no-op
 * regression at worst.
 *
 * `import.meta.env.DEV` (true only for the Vite *dev server*, never for
 * a `vite build` artifact — so this can never leak into Preview/
 * Production) additionally defaults an override-free local session to
 * `'unified'` — see the module doc comment above.
 */
export function resolveFrontendSurface(hostname: string = window.location.hostname): FrontendSurface {
  const override = explicitSurfaceOverride()
  if (override) return override
  if (MARKETING_HOSTNAMES.has(hostname.toLowerCase())) return 'marketing'
  return import.meta.env.DEV ? 'unified' : 'app'
}

// --------------------------------------------------------------- App Base URL

/** Matches this repo's existing Vite dev server default for the SaaS App
 * Surface (see `vite.config.ts`) — the one hardcoded fallback here is a
 * local-loopback address, not a Vistoera domain, so it is exempt from
 * (and does not need to route through) the two-file host contract above. */
const DEFAULT_LOCAL_APP_BASE_URL = 'http://127.0.0.1:5183'

/**
 * Absolute base URL of the SaaS App Surface. This is the ONE place a
 * Marketing CTA ("免费开始使用" / "登录") resolves where it navigates to —
 * never a same-origin `/signup` path from a Marketing page (the two
 * Surfaces are, in Production, two different registrable origins), and
 * never a second hardcoded literal. Reads `VITE_APP_BASE_URL`, the exact
 * same pattern `VITE_API_BASE_URL` / `VITE_SAAS_API_BASE_URL` already use
 * in `frontend/src/api/client.ts` / `frontend/src/saas/api.ts`.
 */
export function getAppBaseUrl(): string {
  const explicit = (import.meta.env.VITE_APP_BASE_URL as string | undefined)?.trim()
  if (explicit) return explicit.replace(/\/+$/, '')
  if (import.meta.env.DEV) return DEFAULT_LOCAL_APP_BASE_URL
  // Never guess a Preview/Production host here — this mirrors the
  // VITE_SAAS_API_BASE_URL Environment Contract incident (see
  // docs/architecture/VISTOERA_ENVIRONMENT_CONTRACT.md §1.2): an unset
  // VITE_APP_BASE_URL in a real deployment is a Config Registry gap that
  // must fail loudly (a console error, an empty/broken link a human will
  // immediately notice), never silently resolve to a guessed-wrong host.
  console.error(
    '[surface] VITE_APP_BASE_URL is not set for this build — Marketing CTA links cannot resolve the ' +
      'SaaS App URL. Set it as a Cloudflare Pages build environment variable for this Pages project.',
  )
  return ''
}

export function getSignupUrl(): string {
  return `${getAppBaseUrl()}/signup`
}

export function getLoginUrl(): string {
  return `${getAppBaseUrl()}/login`
}
